import React, { MouseEventHandler, PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { Icons } from '../../../../../components/atoms/SVG';
import Btn from '../../../../../components/molecules/Btn';
import { ICoordinates } from '../../../../../types/ICoordinates';
import { TooltipPlacement } from '../../../../../components/atoms/Tooltip';
import usePrevious from '../../../../../hooks/usePrevious';

type ConnectedPagesGraphPaginationProps = {
  numberOfChildPages: number;
  currentSetOfPages: number;
  maxPagesPerRow: number;
  coordinatesPreviousButton: ICoordinates;
  onPreviousButtonClick: MouseEventHandler<HTMLButtonElement>;
  coordinatesNextButton: ICoordinates;
  onNextButtonClick: MouseEventHandler<HTMLButtonElement>;
};

export function ConnectedPagesGraphPagination({
  numberOfChildPages,
  currentSetOfPages,
  maxPagesPerRow,
  coordinatesPreviousButton,
  onPreviousButtonClick,
  coordinatesNextButton,
  onNextButtonClick,
  children,
}: PropsWithChildren<ConnectedPagesGraphPaginationProps>) {
  const { t } = useSonTranslation('ConnectedPagesGraph');

  const previousCurrentSetOfgPages = usePrevious(currentSetOfPages);
  const previousButtonReference = useRef<HTMLButtonElement>(null);
  const nextButtonReference = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (previousCurrentSetOfgPages && previousCurrentSetOfgPages < currentSetOfPages) {
      // If the set has changes
      // we'll add focus to the button that undo the setChange
      if (previousButtonReference.current) {
        previousButtonReference.current.focus();
      }
    } else if (previousCurrentSetOfgPages && previousCurrentSetOfgPages > currentSetOfPages) {
      if (nextButtonReference.current) {
        nextButtonReference.current.focus();
      }
    }
  }, []);

  const hasMorePages = useCallback((): boolean => {
    const pagesCount = numberOfChildPages / maxPagesPerRow;
    return pagesCount > 1 && currentSetOfPages < Math.ceil(pagesCount) - 1;
  }, [numberOfChildPages, currentSetOfPages, maxPagesPerRow]);

  return (
    <>
      {currentSetOfPages > 0 && (
        <Btn
          reference={previousButtonReference}
          rel="prev"
          style={{
            /* stylelint-disable value-keyword-case */
            left: coordinatesPreviousButton.x,
            top: coordinatesPreviousButton.y,
            /* stylelint-disable value-keyword-case */
          }}
          cssClass="connected-pages-graph__pagination-previous"
          text={t('previous')}
          titleText={t('previous')}
          hiddenText
          svgSpriteId={Icons.ARROW_LEFT}
          tooltipPlacement={TooltipPlacement.TOP}
          // Events
          onClick={onPreviousButtonClick}
        />
      )}
      {children}
      {hasMorePages() && (
        <Btn
          reference={nextButtonReference}
          rel="next"
          style={{
            /* stylelint-disable value-keyword-case */
            left: coordinatesNextButton.x,
            top: coordinatesNextButton.y,
            /* stylelint-enable value-keyword-case */
          }}
          cssClass="connected-pages-graph__pagination-next"
          text={t('next')}
          titleText={t('next')}
          hiddenText
          svgSpriteId={Icons.ARROW_RIGHT}
          // Events
          onClick={onNextButtonClick}
        />
      )}
    </>
  );
}
